/**
 * Platform Utilities
 *
 * Centralized platform display logic for logos, names, and icons
 * Used across Bookings, Revenue Dashboard, and other platform-related components
 */

/**
 * Get platform logo path
 * @param {string} platform - Platform key (lowercase)
 * @returns {string|null} Logo image path or null if not available
 */
export const getPlatformLogo = (platform) => {
  const logoMap = {
    'airbnb': '/images/airbnb.svg',
    'booking': '/images/booking.com.svg',
    'bookingcom': '/images/booking.com.svg',  // Handle both variants
    'booking.com': '/images/booking.com.svg', // Legacy bookings carry this spelling
    'gathern': '/images/GatherN.png',
    'whatsapp': '/images/whatsapp.svg',
    'direct': '/images/Host Me logo 2.png',
    'manual': '/images/Host Me logo 2.png',   // Manual = Host Me
    'hostme': '/images/Host Me logo 2.png'    // the third direct value (HOST_ME_PLATFORMS below)
  };
  return logoMap[platform?.toLowerCase()] || null;
};

/**
 * Get platform display name
 * @param {string} platform - Platform key (lowercase)
 * @returns {string} Localized platform display name
 */
export const getPlatformDisplayName = (platform) => {
  const nameMap = {
    'airbnb': 'Airbnb',
    'booking': 'Booking.com',          // Capital B
    'bookingcom': 'Booking.com',       // Handle both variants
    'booking.com': 'Booking.com',      // Legacy bookings carry this spelling
    'expedia': 'Expedia',
    'gathern': 'Gathern',
    'almosafer': 'المسافر',
    'whatsapp': 'واتساب',
    'direct': 'حجز مباشر',
    'manual': 'حجز مباشر',             // Manual = Direct = Host Me
    'hostme': 'حجز مباشر',             // the third direct value (HOST_ME_PLATFORMS below)
    'other': 'أخرى'
  };
  return nameMap[platform?.toLowerCase()] || platform;
};

/**
 * Get fallback icon for platforms without logos
 * @param {string} platform - Platform key (lowercase)
 * @returns {string} Bootstrap icon class name
 */
export const getPlatformIcon = (platform) => {
  const iconMap = {
    'airbnb': 'bi-house-heart',
    'booking': 'bi-building',
    'bookingcom': 'bi-building',
    'expedia': 'bi-airplane',
    'gathern': 'bi-geo-alt',
    'almosafer': 'bi-compass',
    'whatsapp': 'bi-whatsapp',
    'direct': 'bi-star-fill',
    'manual': 'bi-pencil-square',
    'other': 'bi-globe'
  };
  return iconMap[platform?.toLowerCase()] || 'bi-globe';
};

/**
 * Get platform color for badges/charts
 * @param {string} platform - Platform key (lowercase)
 * @returns {string} Hex color code
 */
export const getPlatformColor = (platform) => {
  // Host Me's own bookings wear the brand purple. Booking.com uses a brighter blue than its dark
  // brand navy (#003580), which sat too close to that purple on a calendar bar; #0071C2 keeps
  // white text readable (5.1:1) and stays far from Gathern's teal (founder's call, 2026-09-15).
  const colors = {
    'airbnb': '#FF5A5F',
    'booking': '#0071C2',
    'bookingcom': '#0071C2',
    'booking.com': '#0071C2',
    'gathern': '#00A699',
    'whatsapp': '#25D366',
    'direct': '#471396',
    'manual': '#471396',
    'hostme': '#471396'
  };
  return colors[platform?.toLowerCase()] || '#6c757d';
};

/**
 * Match a booking's raw platform against a filter dropdown value.
 *
 * "Host Me" is not a single stored value — direct bookings carry sourcePlatform
 * of 'direct', 'manual', or 'hostme'. Selecting "Host Me" (filterValue 'hostme')
 * must match any of them. All other platforms keep the original substring match
 * (airbnb/booking/gathern) so their behaviour is unchanged.
 *
 * @param {string} sourcePlatform - Booking's raw platform value
 * @param {string} filterValue - Selected dropdown value ('', 'airbnb', 'booking', 'hostme', 'gathern')
 * @returns {boolean} true if the booking matches the selected filter
 */
export const HOST_ME_PLATFORMS = ['direct', 'manual', 'hostme'];

export const matchesPlatformFilter = (sourcePlatform, filterValue) => {
  if (!filterValue) return true; // "all platforms"
  const platform = sourcePlatform?.toLowerCase() || '';
  if (filterValue === 'hostme') {
    return HOST_ME_PLATFORMS.includes(platform);
  }
  return platform.includes(filterValue.toLowerCase());
};
